/**
 * Verified Flaha product-family presentation.
 *
 * Product cards intentionally have no external links: the previous React v2
 * surface did not establish product URLs. FlahaSOIL is identified as the
 * active application and sibling products are described as separate runtimes.
 */
import { Box, Chip, Stack, Typography } from "@mui/material";
import AgricultureIcon from "@mui/icons-material/Agriculture";
import GrassIcon from "@mui/icons-material/Grass";
import WaterDropIcon from "@mui/icons-material/WaterDrop";
import type { ReactNode } from "react";

import { FlahaLogo } from "../../../layouts/components/FlahaLogo";
import { GlassSurface } from "../../../layouts/components/GlassSurface";
import { BRAND_HIERARCHY, BRAND_ORG } from "../../../theme/brand";

interface EcosystemEntry {
	name: string;
	scope: string;
	description: string;
	icon: ReactNode;
	accent: "workspace.green" | "workspace.blue" | "workspace.bronze";
	active?: boolean;
}

const PRODUCTS: EcosystemEntry[] = [
	{
		name: "FlahaSOIL",
		scope: "Soil analysis, interpretation and decision support",
		description:
			"The active application: project- and sample-based soil evidence, authoritative physics and chemistry outputs, agronomic interpretation, reports, and traceability.",
		icon: <GrassIcon />,
		accent: "workspace.green",
		active: true,
	},
	{
		name: "FlahaCALC",
		scope: "Climate and irrigation calculations",
		description:
			"A separate Flaha PA product. FlahaSOIL currently exposes only a read-only export projection of selected soil outputs; the products are not one integrated runtime.",
		icon: <WaterDropIcon />,
		accent: "workspace.blue",
	},
	{
		name: "FlahaFAST",
		scope: "Fertigation planning",
		description:
			"A separate sibling product for nutrient balance and fertigation planning. Its workflows are not part of the current FlahaSOIL React v2 application.",
		icon: <AgricultureIcon />,
		accent: "workspace.bronze",
	},
];

export function FlahaEcosystemSection() {
	return (
		<Box
			component="section"
			aria-labelledby="flaha-ecosystem-title"
			sx={{ py: { xs: 5, md: 8 } }}
		>
			<GlassSurface
				intensity="strong"
				depth="floating"
				sx={{ p: { xs: 2.5, md: 4 } }}
			>
				<Stack spacing={2.5} alignItems={{ xs: "flex-start", md: "center" }}>
					<FlahaLogo size={52} variant="family" />
					<Stack
						spacing={1}
						sx={{ maxWidth: 820, textAlign: { xs: "left", md: "center" } }}
					>
						<Typography
							variant="overline"
							sx={{ color: "workspace.green", letterSpacing: "0.12em" }}
						>
							{BRAND_HIERARCHY}
						</Typography>
						<Typography
							id="flaha-ecosystem-title"
							component="h2"
							variant="h3"
						>
							Focused products in the {BRAND_ORG} family
						</Typography>
						<Typography sx={{ color: "workspace.textSecondary" }}>
							FlahaSOIL, FlahaCALC, and FlahaFAST are Flaha PA decision-support
							products under {BRAND_ORG}. Product-family alignment does not
							imply a single shared application runtime.
						</Typography>
					</Stack>
				</Stack>

				<Box
					sx={{
						display: "grid",
						gridTemplateColumns: {
							xs: "minmax(0, 1fr)",
							md: "repeat(3, minmax(0, 1fr))",
						},
						gap: 2,
						mt: 4,
					}}
				>
					{PRODUCTS.map((product) => (
						<Box
							component="article"
							key={product.name}
							sx={{
								minWidth: 0,
								height: "100%",
								p: 2.5,
								borderRadius: 1.75,
								border: "1px solid",
								borderColor: product.active
									? product.accent
									: "workspace.border",
								backgroundColor: product.active
									? "rgba(119,169,87,0.09)"
									: "rgba(246,240,227,0.035)",
							}}
						>
							<Stack direction="row" spacing={1.5} alignItems="flex-start">
								<Box
									aria-hidden="true"
									sx={{
										display: "grid",
										placeItems: "center",
										width: 42,
										height: 42,
										flexShrink: 0,
										borderRadius: 1.5,
										color: product.accent,
										backgroundColor: "rgba(246,240,227,0.06)",
									}}
								>
									{product.icon}
								</Box>
								<Box sx={{ minWidth: 0 }}>
									<Stack
										direction="row"
										flexWrap="wrap"
										gap={1}
										alignItems="center"
									>
										<Typography component="h3" variant="h6">
											{product.name}
										</Typography>
										{product.active && (
											<Chip label="Active product" size="small" color="success" />
										)}
									</Stack>
									<Typography
										variant="caption"
										sx={{ color: product.accent, fontWeight: 700 }}
									>
										{product.scope}
									</Typography>
								</Box>
							</Stack>
							<Typography
								variant="body2"
								sx={{ color: "workspace.textSecondary", mt: 2 }}
							>
								{product.description}
							</Typography>
						</Box>
					))}
				</Box>
			</GlassSurface>
		</Box>
	);
}
